import { z } from 'zod';

import { NEW_CARS_FILTER } from '@/constants/new-cars/new-cars.constants';
import {
  KIA_BRAND_ID,
  STATUS_SPECIALS,
} from '@/constants/specials/specials.constants';
import { apiService, directusService, zodKeys } from '@/services/api.service';
import { getImageLink } from '@/services/link.service';
import { getAllAvailableModelData } from '@/services/model.service';
import { getAllAvailableSpecials } from '@/services/SpecialsService.service';
import {
  BalloonTypes,
  FinanceFilterState,
} from '@/types/filters/filters.types';
import {
  NewCarListing,
  NewCarsLandingPage,
} from '@/types/new-cars/new-cars.types';
import { SpecialFor, VehicleType } from '@/types/used-cars/used-cars.types';
import { deepCopy } from '@/utils/general';

import { getSpecificationsByDuoportaId } from '../specials/specials.service';
export const getNewCarsPage = async (): Promise<NewCarsLandingPage> => {
  const { data } = await directusService.get('/items/new_cars_landing', {
    params: {
      fields: [
        'title',
        'subtitle',
        'footer_call_to_action.*',
        'footer_call_to_action_image',
        'build_and_price_background',
        'seo_data.*',
      ],
    },
  });

  return {
    title: data.title,
    subtitle: data.subtitle,
    footer_call_to_action: data.footer_call_to_action,
    footer_call_to_action_image: getImageLink(data.footer_call_to_action_image),
    seo_data: data.seo_data,
    build_and_price_background: getImageLink(data.build_and_price_background),
  };
};

type Filter = {
  start_date?: { [key: string]: string };
  end_date?: { [key: string]: string };
  status?: { [key: string]: string };
  dealerships?: any;
  vehicle_type?: { [key: string]: VehicleType.New | VehicleType.Fleet };
  brand?: { [key: string]: number };
  special_for?: { [key: string]: SpecialFor.Vehicle };
  national_special?: { [key: string]: boolean };
  range?: { [key: string]: boolean } | string;
  _and?: Filter[];
  _or?: Filter[];
  _eq?: Filter[];
};

const baseFilter: { _and: Filter[] } = {
  _and: [
    {
      ...STATUS_SPECIALS,
      special_for: { _eq: SpecialFor.Vehicle },
      vehicle_type: { _eq: VehicleType.New },
    },

    { vehicle_type: { _neq: VehicleType.Fleet } },
  ],
};

export const getNewCarDetailsListingOverview = async (
  filters = {},
  dealershipId: string | null = null,
): Promise<NewCarListing[]> => {
  const { data }: { data: NewCarListing[] } = await directusService.get(
    '/items/cms_new_cars',
    {
      params: {
        fields: [
          'id',
          'main_title',
          'image_no_background_side',
          'hide_in_build_and_price',
          'image_no_background_left_angle',
          'images_fuel_consumption',
          'price_from',
          'price_disclaimer',
          'images_emmission',
          'co2_emissions',
          'co2_emissions_heading',
          'intro_subheading',
          'body_type.title',
          'body_type.value',
          'specification_prices',
          'fleet_features',
          'image_alt_tag',
          'range',
          'range.id',
          'range.name',
        ],
        filter: NEW_CARS_FILTER,
      },
    },
  );
  const filter = deepCopy(baseFilter) as { _and: Filter[] };

  if (dealershipId) {
    filter._and.push({
      _or: [
        { national_special: { _eq: true } },
        {
          dealerships: {
            dealerships_id: { _eq: dealershipId },
          },
        },
      ],
    });
  } else {
    filter._and.push({ national_special: { _eq: true } });
    filter._and.push({ brand: { _eq: KIA_BRAND_ID } });
    filter._and.push({ range: { _nnull: true } });
  }
  const updatedFiler = {
    ...filter,
    ...filters,
  };

  const params = {
    updatedFiler,
    fields:
      'id,title,sub_title,cashback,image,monthly_repayment,payment_terms,deposit,residual,interest_rate,vehicle_type,range,range.id,range.name,mmcode',
    limit: -1,
  };

  const availableSpecials = await getAllAvailableSpecials(updatedFiler, params);
  const mmCodes: number[] = [];
  const newestYears: number[] = [];

  // make a deep copy of the original result as we're about to modify it,
  // this function can run multiple times on the same result so we need to make a copy
  const dataCopy = deepCopy(data) as NewCarListing[];

  dataCopy.forEach((car) => {
    let isPromotional = false;
    let lowestPrice = Number.MAX_VALUE;
    let lowestPriceYear: string | null = null;
    let mmCode: string | null = null;
    const specialsMap = new Map();

    const threeMonthsAgoDate = new Date();

    threeMonthsAgoDate.setMonth(new Date().getMonth() - 3);

    car.specification_prices?.forEach((spec) => {
      const specsForThisCar = availableSpecials.filter(
        (detailCar) =>
          detailCar.mmcode === spec.mm_code ||
          detailCar.range?.id === car.range?.id,
      );

      if (specsForThisCar.length > 0) {
        isPromotional = true;
        specsForThisCar.forEach((special) => {
          if (!specialsMap.has(special.id)) {
            specialsMap.set(special.id, special);
          }
        });
      }

      const price = parseInt(spec.price);

      if (price < lowestPrice) {
        lowestPrice = price;
        lowestPriceYear = spec.year;
        mmCode = spec.mm_code;
      }
    });

    car.isPromotional = isPromotional;

    if (isPromotional) {
      car.promotionalSpecials = Array.from(specialsMap.values());
    }
    if (mmCode !== null && lowestPriceYear !== null) {
      mmCodes.push(parseInt(mmCode));
      newestYears.push(lowestPriceYear);
      car.lowestSpecMmCode = mmCode;
    }
  });
  const modelFilters = {
    mmcode: { _in: mmCodes },
    year: { _in: newestYears },
    status: { _eq: 'published' },
  };

  const models = await getAllAvailableModelData(modelFilters, 'year');

  const mmcodeMap: Record<string, any> = {};

  models.forEach((car) => {
    if (!mmcodeMap[car.mmcode] || mmcodeMap[car.mmcode].year < car.year) {
      mmcodeMap[car.mmcode] = car;
    }
  });

  const modifiedCars: NewCarListing[] = dataCopy.map((newCar) => {
    const updatedCar = mmcodeMap[newCar.lowestSpecMmCode || ''];

    const newCarsUpdates = {
      ...(updatedCar ? mmcodeMap[newCar.lowestSpecMmCode as string] : {}),
      ...newCar,
      lowestSpecfuelType:
        updatedCar && !newCar.lowestSpecfuelType
          ? updatedCar.fuel_type
          : newCar.lowestSpecfuelType,
    };

    return newCarsUpdates;
  });

  return modifiedCars;
};

export const specificationOfModelsByDuoportaIds = async (
  duoportalaIds: string[],
) => {
  const data = duoportalaIds?.map(async (id) => {
    const specifications = await getSpecificationsByDuoportaId(id);

    return specifications;
  });

  const result = await Promise.allSettled(data);

  return result;
};

interface FinanceCalculationParams {
  deposit: number;
  interestRate: number;
  months: number;
  carLoanDate: string;
  balloonAmount: number;
  isBalloonPercentage: boolean;
  deliveryFee: number;
  monthlyInstalmentSaleFee: number;
  initiationFee: number;
  carPrice: number;
}

const VehicleLoanSchema = z.object({
  id: z.number(),
  year_offset: z.number(),
  interest_rate: z.number(),
  term: z.number(),
  deposit: z.number(),
  baloon_payment: z.number(),
  initiation_fee: z.number(),
  monthly_instalment_sale_fee: z.number(),
  delivery_fee: z.number(),
});

export type DefaultRepaymentElement = z.infer<typeof VehicleLoanSchema>;

const repaymentResultSchema = z.object({
  fees: z.number().positive(),
  registration_fee: z.number().positive(),
  monthlyPayment: z.number().positive(),
  loanAmount: z.number().positive(),
  totalRepayment: z.number().positive(),
  totalInterest: z.number().positive(),
  estimatedPayoffDate: z.string(),
  effectiveAnnualRate: z.string(),
});

export const getInstallementValue = async ({
  financeFilter,
  balloonOption,
  defaultValues,
}: {
  financeFilter: FinanceFilterState;
  balloonOption: BalloonTypes;
  defaultValues: z.infer<typeof VehicleLoanSchema>;
}) => {
  const currentDate: string = new Date().toISOString().split('T')[0] ?? '';

  const params: FinanceCalculationParams = {
    carPrice: financeFilter.price,
    deposit: financeFilter.deposit,
    interestRate: financeFilter.interest,
    months: financeFilter.paymentTerm,
    carLoanDate: currentDate,
    balloonAmount: financeFilter.balloon,
    isBalloonPercentage: balloonOption === BalloonTypes.percentage,
    deliveryFee: defaultValues.delivery_fee,
    monthlyInstalmentSaleFee: defaultValues.monthly_instalment_sale_fee,
    initiationFee: defaultValues.initiation_fee,
  };

  const calculationResults = await apiService.get('/vehicle-loan-calculation', {
    params,
  });

  const result = repaymentResultSchema.safeParse(calculationResults);

  if (result.success) {
    return result.data;
  }
  return null;
};

export const getDefaultVehicleLoanValueByPriceYear = async (
  price: number | string,
  year: string | string,
) => {
  const numberPrice = Number(price);
  const pricePerMonth = await apiService.get(
    `/vehicle-loan-calculation/${year}?price=${numberPrice}`,
  );
  const result = repaymentResultSchema.safeParse(pricePerMonth);

  if (result.success) {
    return result.data;
  }
  return null;
};

export type VehicleLoanValues = Awaited<
  ReturnType<typeof getDefaultVehicleLoanValueByPriceYear>
>;

export const getDefaultMontlyRepaymentValues = async () => {
  const { data } = await directusService.get(
    '/items/monthly_repayment_default_overrides',
    {
      params: {
        fields: zodKeys(VehicleLoanSchema),
      },
    },
  );

  const VehicleLoanSchemaArray = z.array(VehicleLoanSchema);
  const result = VehicleLoanSchemaArray.safeParse(data);

  if (result.success) {
    return result.data;
  }
  return null;
};

export type DefaultRepaymentValue = Awaited<
  ReturnType<typeof getDefaultMontlyRepaymentValues>
>;
